// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AutocompleteItemModel,
    AutocompleteModel,
    ProductAutocompleteItemModel,
    ProductModel,
    RealTimePricingModel,
    UnitOfMeasureModel,
} from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";

import FancyScroll, { FancyScrollRefProps } from "@conklin/App/Components/Common/FancyScroll";
import updateAutoCompleteProductInfo from "@conklin/App/Handlers/GetAutoCompleteModel/UpdateAutoCompleteProductInfo";
import { xHeaderUpdate } from "@conklin/App/Handlers/XNG/Header";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import TextField, { TextFieldPresentationProps } from "@conklin/MobiusOverrides/TextField/TextField";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@conklin/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@conklin/Overrides/Widgets/Header/SearchHistory";
import { HEADER_HEIGHT_CSS_VAR } from "@conklin/XDL/constants";
import { parseBoolean } from "@conklin/XDL/Utilities";
import getBoldedText from "@insite/client-framework/Common/Utilities/getPatternBolded";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import produce from "immer";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import { startSearch } from "@insite/client-framework/Store/CommonHandlers/StartSearch";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import FloatingMenuTooltip from "@conklin/App/Components/Menu/FloatingMenuTooltip";
import { FloatingTooltipStyles } from "@conklin/App/Components/FloatingTooltip/FloatingTooltip";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
        productSettings,
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNg-Changes
        mobileSearchOpen: state.xHeader.mobileSearchOpen,
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        productSettings,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    startSearch,
    // XNg-Changes
    xHeaderUpdate,
    loadRealTimePricing,
    updateAutoCompleteProductInfo,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    // autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;

    // XNG-Changes
    autoCompleteModel?: AutocompleteModel & { productContexts: ProductContextModel[] };
    isAutocompleteOpen?: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface CustomProductUnitOfMeasure {
    productUnitOfMeasureId: string;
    unitOfMeasure: string;
    unitOfMeasureDisplay: string;
    description: string;
    qtyPerBaseUnitOfMeasure: number;
    roundingRule: string;
    isDefault: boolean;
}

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    autocompleteColumnProductMobileDivider?: InjectableCss;
    popup?: FloatingTooltipStyles;
    inputWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            // justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

let autoCompleteRequestTimestamp: number;

// XNG-Changes
let realTimeRequestTimestamp: number;
class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private inputWrapperRef = createRef<HTMLDivElement>();
    parentBoxRef = React.createRef<HTMLDivElement>();
    fancyScrollRef = React.createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.

                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        this.props.startSearch({
            query: this.state.query,
            autocompleteModel: this.state.autoCompleteModel,
            searchPath: this.props.searchPath,
            goToUrl: this.goToUrl,
            searchHistoryEnabled: this.props.searchHistoryEnabled,
            searchHistoryLimit: this.props.searchHistoryLimit,
        });
    };

    loadAutocomplete = debounce((query: string) => {
        const currentRequestTimestamp = (autoCompleteRequestTimestamp = new Date().getTime());
        this.props.getAutocompleteModel({
            query,
            onSuccess: (result: any) => {
                if (currentRequestTimestamp !== autoCompleteRequestTimestamp) {
                    return;
                }
                let autoCompleteResult: AutocompleteModel = {} as AutocompleteModel;
                if (this.showAutocomplete) {
                    autoCompleteResult = {
                        ...result,
                        productContexts: (result?.products || []).map((product: any) =>
                            this.createProductContext(product, query),
                        ),
                    };
                    this.setState(
                        {
                            autoCompleteModel: autoCompleteResult as any,
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    // <XNG-Changes>
                    this.updatePopoverVisibility(autoCompleteResult);

                    // </XNG-Changes>
                    // this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    updateAutoCompleteProductInfo = ({
        productId,
        result,
        type,
        newValue,
    }: {
        productId: string;
        result: RealTimePricingModel;
        type: "uom" | "qty";
        newValue: string | number;
    }) => {
        this.setState(
            (prevState: any) => {
                const productContexts = prevState.autoCompleteModel?.productContexts.map((productState: any) => {
                    if (productState.product.id === productId) {
                        if (type === "uom") {
                            productState.productInfo.pricing = result.realTimePricingResults?.[0];
                            productState.productInfo.unitOfMeasure = result.realTimePricingResults?.[0].unitOfMeasure;
                        }
                        if (type === "qty") {
                            productState.productInfo.qtyOrdered = newValue;
                        }
                    }
                    return productState;
                });
                return {
                    ...prevState,
                    autoCompleteModel: {
                        ...prevState.autoCompleteModel,
                        productContexts: cloneDeep(productContexts),
                    },
                };
            },
            () => {
                this.fancyScrollRef?.current?.updateScrollable();
            },
        );
    };

    createProductContext = (product: ProductAutocompleteItemModel, query: string) => {
        const customUnitOfMeasures = JSON.parse(
            product.properties.unitOfMeasures || "[]",
        ) as CustomProductUnitOfMeasure[];
        const unitOfMeasures: UnitOfMeasureModel[] = customUnitOfMeasures.map(({ productUnitOfMeasureId, ...uom }) => ({
            id: productUnitOfMeasureId,
            ...uom,
        }));
        const unitOfMeasure = unitOfMeasures.find(o => !!o.isDefault)?.unitOfMeasure || "EA";

        const productInfo: any = {
            productId: product.id!,
            productDetailPath: product.url,
            qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
            unitOfMeasure,
            pricing: product.properties.pricing,
            failedToLoadPricing: product.properties.failedToLoadPricing,
        };

        const productModel: any = {
            id: product.id!,
            productNumber: product.displayErpNumber,
            customerProductNumber: product.displayName,
            mediumImagePath: product.image,
            productTitle: product.displayTitle,
            canonicalUrl: product.url,
            uri: product.uri,
            canAddToCart: parseBoolean(product.properties.canAddToCart),
            manufacturerItem: product.displayManufacturerItemNumber,
            minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
            quoteRequired: parseBoolean(product.properties.isQuoteRequired),
            detail: {
                sku: getBoldedText(product.properties.sku, query),
                multipleSaleQty: product.properties.multipleSaleQty,
            },
            brand: {
                name: product.brandName,
                detailPagePath: product.brandDetailPagePath,
            },
            properties: {
                ...product.properties,
            } as any,
            unitOfMeasures,
        };

        const updateProductInfo = (value: string | number, infoType: "uom" | "qty") => {
            const newValue = infoType === "uom" ? { unitOfMeasure: value as string } : { qtyOrdered: value as number };
            if (infoType === "uom") {
                this.props.updateAutoCompleteProductInfo({
                    productId: product.id!,
                    ...newValue,
                    onSuccess: result => {
                        this.updateAutoCompleteProductInfo({
                            productId: product.id!,
                            result,
                            type: infoType,
                            newValue: value,
                        });
                    },
                });
            } else {
                this.updateAutoCompleteProductInfo({
                    productId: product.id!,
                    result: {
                        realTimePricingResults: null,
                        uri: "",
                        properties: {},
                    },
                    type: infoType,
                    newValue: value,
                });
            }
        };

        const newProduct: ProductContextModel = {
            product: { ...productModel },
            productInfo,
            onQtyOrderedChanged: qty => updateProductInfo(qty, "qty"),
            onUnitOfMeasureChanged: uom => updateProductInfo(uom, "uom"),
        };

        return newProduct;
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);

        // XNG-Changes
        if (newQuery === "") {
            this.closeAutocomplete();
        } else {
            this.updatePopoverVisibility(this.state.autoCompleteModel);
        }
    };

    openAutocomplete = () => {
        // this.popover.current && (this.popover.current! as any).openPopover();
        this.setState({ isAutocompleteOpen: true });
    };

    closeAutocomplete = () => {
        // this.popover.current && (this.popover.current! as any).closePopover();
        this.setState({ isAutocompleteOpen: false });
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    // XNG-Changes: update event type
    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    // <XNG-Changes>
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        if (this.props.isTabletScreen && !this.props.mobileSearchOpen) {
            this.props.xHeaderUpdate({ key: "mobileSearchOpen", value: true });
        }
    };

    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };
    // </XNG-Changes>

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        // XNG-Changes
        if (this.fancyScrollRef.current) {
            this.fancyScrollRef.current.updateScrollable();
        }

        const {
            categories,
            brands,
            content,
            productContexts,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;
        const hasContent =
            (categories && categories.length > 0) || (brands && brands.length > 0) || (content && content.length > 0);
        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}

                {!isRetailSearchCompletionResults && (
                    <StyledWrapper
                        {...this.styles.autocompleteWrapper}
                        {...{
                            width: this.inputWrapperRef.current?.getBoundingClientRect().width || 300,
                            hasContent,
                        }}
                        data-test-selector="autocomplete-container"
                        className="autocomplete-wrapper"
                    >
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper"
                            >
                                <AutocompleteCategories
                                    categories={categories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                />
                                <AutocompleteBrands
                                    brands={brands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                />
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            </StyledWrapper>
                        )}
                        {productContexts &&
                            productContexts.length > 0 &&
                            (this.props.isTabletScreen ? (
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnProductWrapper}
                                    className="autocomplete-column-wrapper"
                                    {...{ hasContent }}
                                    ref={this.parentBoxRef}
                                >
                                    <AutocompleteProducts
                                        products={productContexts}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteProductsStyles}
                                        // XNG-Changes
                                        {...{ doSearch: this.doSearch, productSettings: this.props.productSettings }}
                                    />
                                </StyledWrapper>
                            ) : (
                                <>
                                    <FancyScroll
                                        refElement={this.parentBoxRef}
                                        ref={this.fancyScrollRef}
                                        isBoxShadow
                                        shadowHeight="24px"
                                    >
                                        <StyledWrapper
                                            {...this.styles.autocompleteColumnProductWrapper}
                                            className="autocomplete-column-wrapper"
                                            {...{ hasContent }}
                                            ref={this.parentBoxRef}
                                        >
                                            <AutocompleteProducts
                                                products={productContexts}
                                                focusedItem={this.state.focusedAutocompleteItem}
                                                goToUrl={this.goToUrl}
                                                extendedStyles={this.styles.autocompleteProductsStyles}
                                                // XNG-Changes
                                                {...{
                                                    doSearch: this.doSearch,
                                                    productSettings: this.props.productSettings,
                                                }}
                                            />
                                        </StyledWrapper>
                                    </FancyScroll>
                                </>
                            ))}
                        {/* XNG-Changes */}
                        {productContexts && productContexts.length > 0 && hasContent && !this.props.isMobileScreen && (
                            <StyledWrapper {...this.styles.autocompleteColumnProductMobileDivider} />
                        )}
                    </StyledWrapper>
                )}
            </>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        const searchHistoryItems = getSearchHistory();
        const hasSearchHistory = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        if (!this.state.query && hasSearchHistory) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    extendedStyles={styles.searchHistoryStyles}
                />
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };

    render() {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;
        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} style={{ position: "relative" }}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    placeholder={translate("Search by Keyword")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    // XNG-Changes
                    onKeyDown={this.handlePopoverKeyDown}
                    aria-multiline="false"
                    aria-required="false"
                    aria-expanded={this.state.isAutocompleteOpen}
                    onFocus={this.handleInputFocus}
                    maxLength={100}
                    showClear={true}
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: change popover with tippy */}
                <FloatingMenuTooltip
                    trigger="manual"
                    isOpen={this.state.isAutocompleteOpen}
                    triggerComponent={searchInput}
                    overlay={true}
                    showArrow={false}
                    placement="bottom-start"
                    onOpenChange={open => this.setState({ isAutocompleteOpen: open })}
                    extendedStyles={styles.popup}
                    offset={this.props.isTabletScreen ? 11 : 0}
                    shift={{
                        padding: 0,
                    }}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
